import {
  EntityStateSetActionCodec,
  EntityStateViewName,
  isEntityStateKey,
  isEntityStateValue,
  isEntityStateViewName,
} from '@engoo/realtime-protocol/entityState';
import { KeyPathViewElementCodec } from '@engoo/realtime-protocol/keypath';
import { parseStateKey, KeyPath } from 'app/models/entityStateSync/keypath';
import {
  CompactVocabState,
  CourseListingState,
  KeyPathRootState,
  KeyPathState,
  LessonPageState,
  VocabSentencesState,
  ChatAttachmentState,
} from 'app/models/entityStateSync/types';

const update = <ViewName extends EntityStateViewName>(
  keyPathState: KeyPathState<ViewName>,
  viewName: ViewName,
  key: unknown,
  value: unknown,
) => {
  if (isEntityStateKey(viewName, key) && isEntityStateValue(viewName, key, value)) {
    return keyPathState.set(key, value);
  } else {
    return keyPathState;
  }
};

const applyState = (
  state: KeyPathRootState,
  path: KeyPath,
  viewName: EntityStateViewName,
  key: unknown,
  value: unknown,
): KeyPathRootState => {
  switch (viewName) {
    case EntityStateViewName.CompactVocab: {
      return state.update(EntityStateViewName.CompactVocab, (s) => {
        return s.update(path, (keyState = new CompactVocabState()) => {
          return update(keyState, EntityStateViewName.CompactVocab, key, value);
        });
      });
    }

    case EntityStateViewName.CourseListing: {
      return state.update(EntityStateViewName.CourseListing, (s) => {
        return s.update(path, (keyState = new CourseListingState()) => {
          return update(keyState, EntityStateViewName.CourseListing, key, value);
        });
      });
    }

    case EntityStateViewName.LessonPage: {
      return state.update(EntityStateViewName.LessonPage, (s) => {
        return s.update(path, (keyState = new LessonPageState()) => {
          return update(keyState, EntityStateViewName.LessonPage, key, value);
        });
      });
    }

    case EntityStateViewName.VocabSentences: {
      return state.update(EntityStateViewName.VocabSentences, (s) => {
        return s.update(path, (keyState = new VocabSentencesState()) => {
          return update(keyState, EntityStateViewName.VocabSentences, key, value);
        });
      });
    }

    case EntityStateViewName.ChatAttachment: {
      return state.update(EntityStateViewName.ChatAttachment, (s) => {
        return s.update(path, (keyState = new ChatAttachmentState()) => {
          return update(keyState, EntityStateViewName.ChatAttachment, key, value);
        });
      });
    }
  }
};

export const entityStateReducer = (
  state: KeyPathRootState = new KeyPathRootState(),
  action?: unknown,
): KeyPathRootState => {
  if (EntityStateSetActionCodec.is(action)) {
    const path = [...action.path];

    // Possibly a little unintuitive, but the last key path element dictates
    // decides which bucket the state belongs to. Pull it out before casting the
    // path to an immutable structure.
    const lastKeyPathElement = path.pop();
    const keyPath = parseStateKey(path);

    if (!KeyPathViewElementCodec.is(lastKeyPathElement)) {
      return state;
    }

    const { viewName } = lastKeyPathElement;

    // Verify that the viewname/key/value is legitimate.
    if (isEntityStateViewName(viewName)) {
      return applyState(state, keyPath, viewName, action.key, action.value);
    }

    return state;
  } else {
    return state;
  }
};
